import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { saveQuote } from '../features/quotes/useQuotes';
import PrintableQuote from './PrintableQuote';
import { createLeadFromCalculator } from '../features/leads/api/createLeadFromCalculator';
import { Calculator, Printer, User, Send, CalendarCheck, CheckCircle2, ExternalLink, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import Header from './Header';
import ClientInfoForm from './ClientInfoForm';
import GoogleMapMeasurement from './GoogleMapMeasurement';
import ServiceCalculatorDashboard from './ServiceCalculatorDashboard';
import QuoteSummary from './QuoteSummary';
import PropertyInfoPanel from './PropertyInfoPanel';
import SaveStaleGuardModal from './calculator/SaveStaleGuardModal';
import ClearCalculatorButton from './calculator/ClearCalculatorButton';
import { getStaleServices, isCalculationStale, SERVICE_LABELS } from '../utils/calculatorStaleness';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { computeTotals } from '../utils/pricing/roofPriceMath';
import { calculateRoofPricingBackend } from '../services/pricingService';
import { useCompanyBranding } from '../hooks/useCompanyBranding';
import { auth } from '../firebase';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';

import { useSearchParams, useNavigate } from 'react-router-dom';

import { getClient } from '../features/clients/api/getClient';
import { loadLeadForPrefill } from '../features/clients/api/loadLeadForPrefill';
import { useCompany } from '../context/CompanyContext';

const RoofEstimator = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const clientIdFromUrl = searchParams.get('clientId') || null;
  const leadIdFromUrl = searchParams.get('leadId') || null;
  const { companyId } = useCompany();

  // When the Calculator is launched from a Customer Profile (`?leadId=…`),
  // we are operating in CONTEXTUAL mode — the lead already exists. All save
  // paths must reuse this id instead of calling createLeadFromCalculator,
  // which would create a duplicate lead. Set by the prefill effect below
  // once the lead has been loaded successfully.
  const [contextLeadId, setContextLeadId] = useState(null);
  // Verified Google metadata carried over from the lead (placeId/lat/lng/…)
  // — exposed so child components (e.g. GoogleMapMeasurement) can re-center
  // on the property without requiring another address search.
  const [prefilledLocation, setPrefilledLocation] = useState(null);

  // ----------------------------
  // 🏢 Company Branding (for PrintableQuote)
  // ----------------------------
  const { branding, loading: brandingLoading } = useCompanyBranding();

  // ----------------------------
  // ✅ Success Modal State (CTA Feedback)
  // ----------------------------
  const [successModal, setSuccessModal] = useState({
    open: false,
    type: null, // 'saved' | 'booked' | 'sms_sent'
    leadId: null,
    clientName: null,
    conversationId: null,
    recipientPhone: null,
  });

  // ----------------------------
  // 🧾 Client Info
  // ----------------------------
  const [clientInfo, setClientInfo] = useState({
    firstName: '',
    lastName: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    phone: '',
    email: ''
  });

  // Reference to the printable quote for capturing HTML
  const printableQuoteRef = useRef(null);

  // ----------------------------
  // 📏 Roof Data
  // ----------------------------
  const [roofData, setRoofData] = useState({
    sqFt: '',
    material: 'asphalt',
    stage: 'stage1',
    pitch: '4/12',
    stories: '1'
  });

  // ----------------------------
  // ⚙️ Pricing & Multipliers
  // ----------------------------
  // NOTE (Tier 2): Legacy `pricing` state removed — Calculator uses the
  // backend pricing engine via `/api/pricing/calculate/{service}`. Pricing
  // settings live at /settings/pricing.

  const hasPrefilledClient = useRef(false);

  const [pitchMultipliers, setPitchMultipliers] = useState({
    '1/2': 1.0035,
    '2/12': 1.0138,
    '3/12': 1.0308,
    '4/12': 1.0541,
    '5/12': 1.0833,
    '6/12': 1.118,
    '7/12': 1.1577,
    '8/12': 1.2019,
    '9/12': 1.25,
    '10/12': 1.3017,
    '11/12': 1.3566,
    '12/12': 1.4142
  });

  // ----------------------------
  // 👤 Prefill client info from clientId context
  // ----------------------------
  useEffect(() => {
    const loadClient = async () => {
      if (!clientIdFromUrl || hasPrefilledClient.current) return;

      const res = await getClient(clientIdFromUrl);
      if (!res.success || !res.client) return;

      const data = res.client || {};

      // Parse name into first/last
      let firstName = '';
      let lastName = '';
      if (typeof data.name === 'string' && data.name.trim()) {
        const parts = data.name.trim().split(' ');
        firstName = parts[0] || '';
        lastName = parts.slice(1).join(' ') || '';
      }

      // Address may be a string or an object
      let addressLine = '';
      let city = '';
      let state = '';
      let zip = '';

      const addr = data.address;
      if (typeof addr === 'string') {
        addressLine = addr;
      } else if (addr && typeof addr === 'object') {
        addressLine = addr.line1 || addr.street || '';
        city = addr.city || '';
        state = addr.state || '';
        zip = addr.zip || '';
      }

      hasPrefilledClient.current = true;

      setClientInfo((prev) => ({
        ...prev,
        firstName: prev.firstName || firstName,
        lastName: prev.lastName || lastName,
        phone: prev.phone || data.phone || '',
        email: prev.email || data.email || '',
        address: prev.address || addressLine,
        city: prev.city || city,
        state: prev.state || state,
        zip: prev.zip || zip,
      }));
    };

    loadClient();
  }, [clientIdFromUrl]);

  // ----------------------------
  // 👤 Prefill client info from leadId context (Customer Profile launch)
  // ----------------------------
  // When the Calculator is launched from a Customer Profile with
  // `?leadId=…`, we read the same lead doc the profile renders from and
  // prefill the Client Info card. We also stash the leadId in
  // `contextLeadId` so the save paths below reuse it instead of creating a
  // duplicate lead via createLeadFromCalculator.
  //
  // TODO(company-field): When the lead schema grows a `company` field, the
  // loader will return it and this effect should map it into clientInfo.
  useEffect(() => {
    const loadLead = async () => {
      if (!leadIdFromUrl || !companyId || hasPrefilledClient.current) return;

      const res = await loadLeadForPrefill(companyId, leadIdFromUrl);
      if (!res.success || !res.lead) return;

      const lead = res.lead;
      hasPrefilledClient.current = true;
      setContextLeadId(lead.id);
      if (lead.location) setPrefilledLocation(lead.location);

      setClientInfo((prev) => ({
        ...prev,
        firstName: prev.firstName || lead.firstName,
        lastName: prev.lastName || lead.lastName,
        phone: prev.phone || lead.phone,
        email: prev.email || lead.email,
        address: prev.address || lead.address,
        city: prev.city || lead.city,
        state: prev.state || lead.state,
        zip: prev.zip || lead.zip,
      }));
    };

    loadLead();
  }, [leadIdFromUrl, companyId]);

  // ----------------------------
  // 📊 Service Calculations (multi-service)
  // ----------------------------
  const [calculations, setCalculations] = useState({
    roof: null,
    siding: null,
    gutter: null,
    driveway: null,
    deck: null,
    fence: null,
    pest_control: null,
    lawn_care: null,
  });
  const serviceOrder = useMemo(
    () => ['roof', 'siding', 'gutter', 'driveway', 'deck', 'fence', 'pest_control', 'lawn_care'],
    []
  );

  const hasCalculatedService = useMemo(() => {
    return serviceOrder.some((key) => {
      const svc = calculations[key];
      return svc && svc.result && Number(svc.result.grandTotal) > 0;
    });
  }, [calculations, serviceOrder]);

  // Phase 2: Recurring plan — auto-detected from flat-rate service calculations
  const [recurringPlan, setRecurringPlan] = useState(null);

  // Phase 3: Prepay package — opt-in block of pre-purchased visits
  const [prepayPackage, setPrepayPackage] = useState(null);
  useEffect(() => {
    const FLAT_RATE_IDS = ['pest_control', 'lawn_care'];
    const FLAT_RATE_NAMES = { pest_control: 'Pest Control', lawn_care: 'Lawn Care' };
    for (const svcId of FLAT_RATE_IDS) {
      const freq = calculations[svcId]?.input?.frequency;
      if (freq && freq.type !== 'one-time' && freq.intervalDays) {
        setRecurringPlan((prev) => prev ?? {
          active: true,
          sourceService: svcId,
          interval: freq.intervalDays,
          unit: 'days',
          planLabel: `${FLAT_RATE_NAMES[svcId]} Plan`,
          totalOccurrences: null,
          startDate: null,
        });
        return;
      }
    }
    // No recurring flat-rate service → clear plan
    setRecurringPlan(null);
  }, [calculations]);


  const [showSettings] = useState(false);  // Reserved for future inline settings flyout — read-only stub.

  // ----------------------------
  const [taxRate, setTaxRate] = useState(0);   // set from company billing settings on mount only

  // Load company billing settings on mount — tax rate comes from Settings, nowhere else
  useEffect(() => {
    const loadBillingSettings = async () => {
      try {
        const currentUser = auth.currentUser;
        if (!currentUser) return;
        const token = await currentUser.getIdToken(true);
        const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
        const res = await fetch(`${backendUrl}/api/invoices/settings/deposit`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        if (data.taxEnabled) {
          setTaxRate(typeof data.taxRate === 'number' ? data.taxRate : 0);
        } else {
          setTaxRate(0);
        }
      } catch {
        // Non-blocking — silently fall back to 0
      }
    };
    loadBillingSettings();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ----------------------------
  // 📐 Handle Measurement (Tier 2: handleMeasurementChange legacy removed —
  // measurement → input binding now lives in each service panel's auto-fill
  // useEffect against `measurementTotals`.)
  // ----------------------------

  // ----------------------------
  // 🏠 Property Info & Map state
  // ----------------------------
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [mapCenter, setMapCenter] = useState(null);
  const [mapZoom, setMapZoom] = useState(null);

  // ----------------------------
  // 📏 Measurement Totals (from GoogleMapMeasurement)
  // ----------------------------
  const [measurementTotals, setMeasurementTotals] = useState({
    lineFeet: 0,
    areaSqFt: 0,
    perimeterFt: 0,
  });

  // ─────────────────────────────────────────────────────────────────────────
  // Tier 2 — Calculator state sync hardening
  //
  // - panelRefs:        forwardRef'd imperative handles { recalc, getCurrentInputs }
  //                     exposed by each non-Roof service panel. Used by
  //                     `recalculateAllStale()` and (defensively) by recalc-and-save.
  // - panelInputs:      parent-side mirror of each panel's current inputs,
  //                     pushed up via the panel's `onInputsChange` callback.
  //                     Read by `getStaleServices(calculations, panelInputs, …)`
  //                     and used as `initialInputs` to restore a panel that was
  //                     un-toggled then re-toggled.
  // - resetKey:         bump to force every panel to remount with fresh
  //                     `initialInputs` (used by Clear Measurements / Clear
  //                     Calculator). Does NOT clear `calculations`.
  // - manualOverride:   mirrored at parent so Clear Measurements can reset it.
  //                     ServiceCalculatorDashboard still owns the local copy
  //                     until v1+; Tier 2 only reads it via the dashboard's
  //                     callback to trigger resets.
  // ─────────────────────────────────────────────────────────────────────────
  const panelRefs = useRef({
    siding:   { current: null },
    gutter:   { current: null },
    driveway: { current: null },
    deck:     { current: null },
    fence:    { current: null },
  });
  const [panelInputs, setPanelInputs] = useState({});  // { svc: lastKnownInputs }
  const [resetKey, setResetKey] = useState(0);
  const [isRecalcing, setIsRecalcing] = useState(false);
  const [saveGuard, setSaveGuard] = useState({ open: false, staleList: [], onProceed: null });

  // Panels report their current inputs here on every change.
  const handlePanelInputsChange = (svc, inputs) => {
    setPanelInputs((prev) => {
      const cur = prev[svc];
      // Cheap dedupe — avoid setState if the inputs JSON hasn't changed.
      const next = { ...inputs };
      if (cur && JSON.stringify(cur) === JSON.stringify(next)) return prev;
      return { ...prev, [svc]: next };
    });
  };

  // Roof inputs aren't pushed via the same callback (parent owns the calc),
  // so we mirror roofData → panelInputs.roof for the staleness selector.
  useEffect(() => {
    setPanelInputs((prev) => ({ ...prev, roof: { ...roofData } }));
  }, [roofData]);

  // Derived: which services are currently stale (input-diff vs last calc).
  const staleServices = useMemo(
    () => getStaleServices(calculations, panelInputs, null),
    [calculations, panelInputs]
  );

  // Recalculate All Stale — fires each affected panel's imperative `recalc`
  // (or `onCalculateRoof` for the parent-owned roof path), in parallel.
  const recalculateAllStale = async () => {
    if (staleServices.length === 0) {
      toast.info('No services need recalculation');
      return { ok: true, ran: 0 };
    }
    setIsRecalcing(true);
    const ran = [];
    const failed = [];
    try {
      await Promise.all(staleServices.map(async (svc) => {
        try {
          if (svc === 'roof') {
            await calculateRoofTotal();
          } else {
            const ref = panelRefs.current?.[svc]?.current;
            if (ref?.recalc) {
              await ref.recalc();
            } else {
              // FlatRate (pest_control / lawn_care) or any future service
              // without a recalc handle — record so the user knows.
              failed.push(svc);
              return;
            }
          }
          ran.push(svc);
        } catch (e) {
          console.error(`[Calculator] recalc failed for ${svc}:`, e);
          failed.push(svc);
        }
      }));
      if (ran.length > 0) {
        toast.success(
          ran.length === 1
            ? `Recalculated ${SERVICE_LABELS[ran[0]] || ran[0]}`
            : `Recalculated ${ran.length} services`
        );
      }
      if (failed.length > 0) {
        toast.error(
          `Could not recalculate: ${failed.map((s) => SERVICE_LABELS[s] || s).join(', ')}`
        );
      }
      return { ok: failed.length === 0, ran: ran.length, failed };
    } finally {
      setIsRecalcing(false);
    }
  };

  // Clear Calculator — full reset; preserves clientInfo only.
  const clearCalculator = () => {
    setMeasurementTotals({ lineFeet: 0, areaSqFt: 0, perimeterFt: 0 });
    setRoofData({ sqFt: '', material: 'asphalt', stage: 'stage1', pitch: '4/12', stories: '1' });
    setCalculations({
      roof: null, siding: null, gutter: null, driveway: null,
      deck: null, fence: null, pest_control: null, lawn_care: null,
    });
    setPanelInputs({});
    setRecurringPlan(null);
    setPrepayPackage(null);
    setSelectedAddress(null);
    setMapCenter(null);
    setMapZoom(null);
    setResetKey((k) => k + 1);  // force every panel to remount fresh
    toast.success('Calculator cleared');
  };

  // Called by GoogleMapMeasurement.clearAll — reset upstream inputs but
  // intentionally KEEP `calculations` so the user doesn't lose a quote they
  // may want to re-draw the same shape for.
  const handleClearMeasurements = () => {
    setRoofData((prev) => ({ ...prev, sqFt: '' }));  // clear roof's auto-filled sqFt
    setPanelInputs({});                              // clear all panel input mirrors
    setResetKey((k) => k + 1);                       // remount panels with fresh state
  };
  // ─────────────────────────────────────────────────────────────────────────

  // Sync selected address from Google Maps to clientInfo
  const handleAddressSelected = (addressData) => {
    setSelectedAddress(addressData);
    
    // Sync address components to clientInfo if available
    if (addressData) {
      setClientInfo(prev => ({
        ...prev,
        address: addressData.street || addressData.formatted_address || prev.address,
        city: addressData.city || prev.city,
        state: addressData.state || prev.state,
        zip: addressData.zip || addressData.postalCode || prev.zip,
      }));
    }
  };

  // ----------------------------
  // 🧮 Calculate Roof Total (backend authoritative)
  // ----------------------------
  const calculateRoofTotal = async () => {
    const sqft = parseFloat(roofData.sqFt) || 0;

    if (sqft === 0) {
      toast.error('Please enter roof size or measure it on the map');
      return;
    }

    try {
      const resp = await calculateRoofPricingBackend({
        sqFt: sqft,
        pitch: roofData.pitch,
        material: roofData.material,
        stage: roofData.stage,
        stories: roofData.stories,
        discount: 0,
      });

      if (!resp || !resp.success || !resp.quote) {
        toast.error(resp?.message || 'Failed to calculate roof pricing from server');
        return;
      }

      const quote = resp.quote;
      const serviceTotal = Number(quote.serviceTotal ?? quote.roofTotal ?? 0);
      const adjustedSqFt = Number(quote.adjustedSqFt ?? 0);
      const discount = Number(quote.discount ?? 0);
      const tax = Number(quote.tax ?? 0);
      const subtotal = Number(quote.subtotal ?? serviceTotal - discount);
      const grandTotal = Number(quote.grandTotal ?? subtotal + tax);
      const nowIso = new Date().toISOString();

      const newRoof = {
        input: {
          sqFt: Number(roofData.sqFt) || 0,
          pitch: roofData.pitch,
          stage: roofData.stage,
          stories: roofData.stories,
          material: roofData.material,
        },
        result: {
          serviceTotal,
          adjustedSqFt,
          discount,
          tax,
          subtotal,
          grandTotal,
        },
        measurement: {
          polygons: null,
          linearFeet: null,
          sqFt: Number(roofData.sqFt) || 0,
          computedAt: nowIso,
        },
      };

      // Expose the latest roof totals on window so RoofCalculatorPanel can
      // call onServiceTotalChange with consistent data, matching the other
      // service panels. This keeps ServiceCalculatorDashboard and the
      // multi-service save/summary flows in sync.
      if (typeof window !== 'undefined') {
        window.__LATEST_ROOF_TOTALS__ = newRoof.result;
      }

      setCalculations(prev => ({
        ...prev,
        roof: newRoof,
      }));

      toast.success(`Roof cost calculated: $${grandTotal.toFixed(2)}`);
    } catch (e) {
      console.warn('[BACKEND PRICING ERROR]', e);
      toast.error('Failed to calculate roof pricing from server');
    }
  };

  // ----------------------------
  // 💰 Calculate Grand Total (Tier 2 — now a real action)
  // ----------------------------
  // Replaces the legacy no-op placeholder. Wired to QuoteSummary's
  // "Recalculate Total" button via `onRecalculate`. Iterates stale services
  // and fires each panel's recalc in parallel. Per-service totals are still
  // produced by the backend pricing engine via the panel's own POST.
  const calculateGrandTotal = async () => {
    await recalculateAllStale();
  };

  // ----------------------------
  // 🖨️ Print Handler
  // ----------------------------
  const handlePrint = async () => {
    if (!hasCalculatedService) {
      toast.error('Please calculate at least one service quote first');
      return;
    }

    if (!clientInfo.firstName || !clientInfo.lastName) {
      toast.error('Please enter client name before printing');
      return;
    }

    // First, save the lead and quote if not already saved. When launched
    // from a Customer Profile, reuse the existing lead — no duplicates.
    const leadRes = await ensureLeadForSave('Quoted');

    if (!leadRes.success || !leadRes.leadId) {
      toast.error(leadRes.message || 'Failed to create lead');
      return;
    }

    const quotePayload = buildQuotePayload({ 
      leadId: leadRes.leadId, 
      clientId: null,
      companyId: leadRes.companyId,
    });
    const resp = await saveQuote(quotePayload);
    if (!resp.success) {
      toast.error('Lead created but failed to save quote');
      return;
    }

    // Capture HTML from the printable quote and save artifact
    try {
      if (printableQuoteRef.current) {
        const quoteHtml = printableQuoteRef.current.outerHTML;
        
        const currentUser = auth.currentUser;
        if (currentUser) {
          const token = await currentUser.getIdToken(true);
          const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
          
          // Save artifact in background - don't block printing
          fetch(`${backendUrl}/api/quote-artifacts/save`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({
              leadId: leadRes.leadId,
              quoteId: resp.quoteId,
              deliveryMethod: 'print',
              html: quoteHtml,
              publicUrl: null,
              sentTo: null,
            }),
          }).catch(err => console.warn('Failed to save print artifact:', err));
        }
      }
    } catch (err) {
      console.warn('Error capturing print HTML:', err);
    }

    window.print();
  };

  const buildClientSnapshot = () => {
    const clientName = [clientInfo.firstName, clientInfo.lastName].filter(Boolean).join(' ') || null;

    return {
      name: clientName,
      phone: clientInfo.phone || null,
      email: clientInfo.email || null,
      address:
        clientInfo.address ||
        [clientInfo.address, clientInfo.city, clientInfo.state, clientInfo.zip]
          .filter(Boolean)
          .join(', ') ||
        null,
    };
  };

  const buildClientPayloadForLeadOrClientCreate = () => ({
    firstName: clientInfo.firstName || '',
    lastName: clientInfo.lastName || '',
    phone: clientInfo.phone || '',
    email: clientInfo.email || '',
    address: clientInfo.address || '',
    city: clientInfo.city || '',
    state: clientInfo.state || '',
    zip: clientInfo.zip || '',
  });

  /**
   * Resolve the lead this save should attach to.
   *
   * If the Calculator was launched from a Customer Profile (`?leadId=…`),
   * `contextLeadId` is set and we MUST reuse it — calling
   * createLeadFromCalculator would create a duplicate lead for the same
   * customer. Otherwise (standalone estimating), fall back to the existing
   * create-from-calculator API.
   *
   * Returns the same `{ success, leadId, companyId }` shape as
   * createLeadFromCalculator so callers don't branch.
   */
  const ensureLeadForSave = async (stage) => {
    if (contextLeadId) {
      return {
        success: true,
        leadId: contextLeadId,
        companyId,
      };
    }
    const clientPayload = buildClientPayloadForLeadOrClientCreate();
    return await createLeadFromCalculator(clientPayload, stage);
  };

  const buildQuotePayload = ({ leadId, clientId, companyId }) => {
    const clientSnapshot = buildClientSnapshot();

    // Build services block using fixed order
    const servicesPayload = {};
    let totalSubtotal = 0;
    let totalTax = 0;

    serviceOrder.forEach((serviceKey) => {
      const svc = calculations[serviceKey];
      if (!svc || !svc.result) return;
      const grand = Number(svc.result.grandTotal) || 0;
      if (grand <= 0) return;

      servicesPayload[serviceKey] = {
        input: { ...(svc.input || {}) },
        result: { ...(svc.result || {}) },
        measurement: svc.measurement || null,
      };

      const subtotal = Number(svc.result.subtotal) || 0;
      const tax = Number(svc.result.tax) || 0;

      totalSubtotal += subtotal;
      totalTax += tax;
    });

    const grandTotal = totalSubtotal + totalTax;

    const globalDiscount = Number(calculations?.discount) || 0;

    return {
      leadId,
      clientId,
      companyId,
      client: clientSnapshot,
      services: servicesPayload,
      totals: {
        subtotal: totalSubtotal,
        tax: totalTax,
        grandTotal,
        discount: globalDiscount,
        taxRate,            // Stage 3: snapshot the rate used for this quote
      },
      // Phase 2: attach recurring plan if office opted in (stored but not yet activated)
      recurringPlan: recurringPlan?.active ? { ...recurringPlan } : null,
      // Phase 3: attach prepay package if opted in (independent of recurring plan)
      prepayPackage: prepayPackage?.active ? { ...prepayPackage } : null,
      status: 'draft',
      calculatorVersion: '1.0.0',
      pricingConfigVersion: '1.0.0',
    };
  };

  // ----------------------------
  // 💾 Save Actions (Realigned UX)
  // ----------------------------

  /**
   * Save as Lead - Persists lead + quote with stage="Quoted"
   * Shows success modal with View Lead option
   */
  const handleSaveAsLead = async () => {
    if (!hasCalculatedService) {
      toast.error('Please calculate at least one service quote first');
      return;
    }

    // Tier 2 — Save Stale Guard. If any selected service's current inputs
    // disagree with its last-calculated snapshot, open the guard modal so
    // the user must explicitly choose Recalculate & Save / Save Anyway /
    // Cancel before the in-flight quote is committed to Firestore.
    if (staleServices.length > 0) {
      setSaveGuard({
        open: true,
        staleList: [...staleServices],
        onProceed: () => _executeSaveAsLead(),
      });
      return;
    }
    await _executeSaveAsLead();
  };

  const _executeSaveAsLead = async () => {
    // Reuse existing lead when launched from Customer Profile.
    const leadRes = await ensureLeadForSave('Quoted');

    if (!leadRes.success || !leadRes.leadId) {
      toast.error(leadRes.message || 'Failed to create lead');
      return;
    }

    const quotePayload = buildQuotePayload({ 
      leadId: leadRes.leadId, 
      clientId: null,
      companyId: leadRes.companyId,
    });
    const resp = await saveQuote(quotePayload);
    if (!resp.success) {
      toast.error('Lead created but failed to save quote');
      return;
    }

    // Show success modal with View Lead option
    const clientName = [clientInfo.firstName, clientInfo.lastName].filter(Boolean).join(' ') || 'New Lead';
    setSuccessModal({
      open: true,
      type: 'saved',
      leadId: leadRes.leadId,
      clientName,
    });
  };

  /**
   * Send Quote via SMS - Complete flow in single API call
   * Creates lead, saves quote, creates conversation, sends SMS
   */
  const handleSendQuoteViaSMS = async () => {
    if (!hasCalculatedService) {
      toast.error('Please calculate at least one service quote first');
      return;
    }

    if (!clientInfo.phone) {
      toast.error('Phone number is required to send SMS');
      return;
    }

    // Build payload for the unified send endpoint
    const payload = {
      client: {
        firstName: clientInfo.firstName || '',
        lastName: clientInfo.lastName || '',
        phone: clientInfo.phone || '',
        email: clientInfo.email || '',
        address: clientInfo.address || '',
        city: clientInfo.city || '',
        state: clientInfo.state || '',
        zip: clientInfo.zip || '',
      },
      services: {},
      totals: {
        subtotal: 0,
        tax: 0,
        grandTotal: 0,
        discount: 0,
        taxRate,            // Stage 3: snapshot the rate used for this quote
      },
      stage: 'Quoted',
      // When the Calculator was launched from a Customer Profile, send the
      // existing leadId so the backend reuses it instead of finding/creating
      // by phone. Prevents duplicate leads for the same customer.
      leadId: contextLeadId || null,
      // Persist the recurring plan (including any linked Service Program) so
      // that when the client approves from the public link, QuoteDetail's
      // approval gate can read it and activate recurrence automatically.
      recurringPlan: recurringPlan?.active ? { ...recurringPlan } : null,
    };

    // Build services and totals
    serviceOrder.forEach((key) => {
      const svc = calculations[key];
      if (!svc || !svc.result) return;
      const grand = Number(svc.result.grandTotal) || 0;
      if (grand <= 0) return;

      payload.services[key] = {
        input: svc.input || {},
        result: {
          serviceTotal: Number(svc.result.serviceTotal) || 0,
          subtotal: Number(svc.result.subtotal) || 0,
          tax: Number(svc.result.tax) || 0,
          grandTotal: grand,
          discount: Number(svc.result.discount) || 0,
        },
        measurement: svc.measurement || null,
      };

      payload.totals.subtotal += Number(svc.result.subtotal) || 0;
      payload.totals.tax += Number(svc.result.tax) || 0;
      payload.totals.grandTotal += grand;
      payload.totals.discount += Number(svc.result.discount) || 0;
    });

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
      const currentUser = auth.currentUser;
      
      if (!currentUser) {
        toast.error('Please log in to send quotes');
        return;
      }

      const token = await currentUser.getIdToken(true);
      
      const res = await fetch(`${backendUrl}/api/calculator/send-quote-sms`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (!res.ok || !data.success) {
        toast.error(data.detail || data.message || 'Failed to send quote');
        return;
      }

      // Show success modal with SMS confirmation
      const clientName = [clientInfo.firstName, clientInfo.lastName].filter(Boolean).join(' ') || 'Customer';
      setSuccessModal({
        open: true,
        type: 'sms_sent',
        leadId: data.leadId,
        clientName,
        conversationId: data.conversationId,
        recipientPhone: data.recipientPhone,
      });

    } catch (err) {
      console.error('Error sending quote via SMS:', err);
      toast.error('Failed to send quote. Please try again.');
    }
  };

  /**
   * Approve Client - Persists lead + quote with stage="Approved"
   * (Approved = quote accepted, awaiting scheduling)
   * Shows success modal with navigation to pipeline
   */
  const handleBookClient = async () => {
    if (!hasCalculatedService) {
      toast.error('Please calculate at least one service quote first');
      return;
    }

    // Reuse existing lead when launched from Customer Profile.
    const leadRes = await ensureLeadForSave('Approved');

    if (!leadRes.success || !leadRes.leadId) {
      toast.error(leadRes.message || 'Failed to book client');
      return;
    }

    const quotePayload = buildQuotePayload({ 
      leadId: leadRes.leadId, 
      clientId: null,
      companyId: leadRes.companyId,
    });
    const resp = await saveQuote(quotePayload);
    if (!resp.success) {
      toast.error('Client booked but failed to save quote');
      return;
    }

    // Show success modal with View Lead option
    const clientName = [clientInfo.firstName, clientInfo.lastName].filter(Boolean).join(' ') || 'New Client';

    // ── Activate the recurring service plan on the newly-booked lead ─────────
    // Approve Client is the only Calculator CTA that activates recurrence + seeds
    // the first job. Save-as-Lead and Send-via-SMS intentionally skip this step.
    if (recurringPlan?.active && leadRes.leadId) {
      try {
        const { activateServicePlanFromQuote } = await import('../utils/activateServicePlanFromQuote');
        // Extract the per-service pre-tax subtotal so the recurring rate
        // reflects only the recurring service — not bundled one-time work.
        const recurringSvc = calculations?.[recurringPlan.sourceService];
        const serviceSubtotal = Number(recurringSvc?.result?.subtotal);
        await activateServicePlanFromQuote(
          leadRes.companyId,
          leadRes.leadId,
          recurringPlan,
          {
            ...(Number.isFinite(serviceSubtotal) ? { serviceSubtotal } : {}),
            ...(resp?.quoteId ? { quoteId: resp.quoteId } : {}),  // Phase 1 lineage
            taxEnabled: taxRate > 0,                               // snapshot recurring tax terms
            taxRate,
          },
        );
      } catch (planErr) {
        console.error('[RoofEstimator] Service plan activation failed (non-fatal):', planErr);
        toast.error('Client booked, but service plan setup failed. Activate it from the lead profile.');
      }
    }

    setSuccessModal({
      open: true,
      type: 'booked',
      leadId: leadRes.leadId,
      clientName,
    });
  };

  // ----------------------------
  // 🧱 UI Layout
  // ----------------------------
  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/30">
      <Header />

      <main className="container mx-auto px-4 py-4 md:py-8 max-w-7xl pb-32 md:pb-8">
        {/* Tier 2 — Clear Calculator CTA (top of page, light-weight) */}
        <div className="flex justify-end mb-3 print-hidden">
          <ClearCalculatorButton onClear={clearCalculator} />
        </div>

        <div className="grid lg:grid-cols-[1fr,400px] gap-4 md:gap-8 items-start">
          {/* Left Column */}
          <div className="space-y-4 md:space-y-6">
            {/* Client Info */}
            <Card className="p-4 md:p-6 shadow-lg border-l-4 border-l-primary print-hidden">
              <div className="flex items-center gap-3 mb-4 md:mb-6">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <User className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h2 className="text-lg md:text-xl font-bold text-secondary">Client Information</h2>
                  <p className="text-xs md:text-sm text-muted-foreground">Enter customer details</p>
                </div>
              </div>
              <ClientInfoForm clientInfo={clientInfo} setClientInfo={setClientInfo} />
            </Card>

            {/* Property Address, Measurement & Info */}
            <Card className="p-4 md:p-6 shadow-lg border-l-4 border-l-secondary print-hidden">
              <div className="flex items-center gap-3 mb-4 md:mb-6">
                <div className="p-2 bg-secondary/10 rounded-lg">
                  <Calculator className="h-5 w-5 text-secondary" />
                </div>
                <div>
                  <h2 className="text-lg md:text-xl font-bold text-secondary">Property & Measurement</h2>
                  <p className="text-xs md:text-sm text-muted-foreground">Search address and measure</p>
                </div>
              </div>

              <Tabs defaultValue="measure" className="w-full">
                <TabsList className="mb-4 w-full grid grid-cols-2">
                  <TabsTrigger value="measure" className="text-sm">Measure</TabsTrigger>
                  <TabsTrigger value="info" className="text-sm">Property Info</TabsTrigger>
                </TabsList>

                <TabsContent value="measure" className="space-y-4">
                  <GoogleMapMeasurement
                    onAddressSelected={handleAddressSelected}
                    selectedAddress={selectedAddress}
                    mapCenter={mapCenter}
                    mapZoom={mapZoom}
                    onMapStateChange={(center, zoom) => {
                      setMapCenter(center);
                      setMapZoom(zoom);
                    }}
                    onMeasurementUpdate={setMeasurementTotals}
                    onClearMeasurements={handleClearMeasurements}
                  />
                </TabsContent>

                <TabsContent value="info" className="space-y-4">
                  <PropertyInfoPanel addressData={selectedAddress} />
                </TabsContent>
              </Tabs>
            </Card>

            {/* Service Calculator Dashboard */}
            <Card className="p-4 md:p-6 shadow-lg border-l-4 border-l-accent print-hidden">
              <ServiceCalculatorDashboard
                roofData={roofData}
                setRoofData={setRoofData}
                onCalculateRoof={calculateRoofTotal}
                measurementTotals={measurementTotals}
                calculations={calculations}
                setCalculations={setCalculations}
                panelRefs={panelRefs.current}
                panelInputs={panelInputs}
                onPanelInputsChange={handlePanelInputsChange}
                resetKey={resetKey}
                taxRate={taxRate}
              />

              {/* Pricing Settings moved to /settings/pricing - Calculator now uses backend pricing only */}
            </Card>
          </div>

          {/* Right Column - Quote Summary */}
          <div className="lg:sticky lg:top-8">
            <QuoteSummary
              clientInfo={clientInfo}
              roofData={roofData}
              calculations={calculations}
              setCalculations={setCalculations}
              onRecalculate={calculateGrandTotal}
              onPrint={handlePrint}
              taxRate={taxRate}
              onSaveQuote={handleSaveAsLead}
              recurringPlan={recurringPlan}
              onRecurringPlanChange={setRecurringPlan}
              prepayPackage={prepayPackage}
              onPrepayPackageChange={setPrepayPackage}
              staleServices={staleServices}
              panelInputs={panelInputs}
              isRecalcing={isRecalcing}
            />
          </div>
        </div>

        {/* Desktop Action Buttons */}
        <div className="hidden md:flex flex-col items-center gap-6 mt-12 print-hidden">
          {/* Primary Actions Row */}
          <div className="flex flex-wrap justify-center gap-3">
            <Button
              onClick={handleSaveAsLead}
              size="lg"
              variant="outline"
              className="font-semibold shadow-md hover:shadow-lg transition-all px-6 py-4 text-base border-2"
              data-testid="save-as-lead-btn"
            >
              Save as Lead
            </Button>

            <Button
              onClick={handleSendQuoteViaSMS}
              size="lg"
              className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold shadow-lg hover:shadow-xl transition-all px-6 py-4 text-base"
              data-testid="send-quote-sms-btn"
            >
              <Send className="mr-2 h-5 w-5" />
              Send Quote via SMS
            </Button>

            <Button
              onClick={handleBookClient}
              size="lg"
              className="bg-green-600 hover:bg-green-700 text-white font-semibold shadow-lg hover:shadow-xl transition-all px-6 py-4 text-base"
              data-testid="book-client-btn"
            >
              <CalendarCheck className="mr-2 h-5 w-5" />
              Approve Client
            </Button>
          </div>

          {/* Secondary Actions Row */}
          <div>
            <Button
              onClick={handlePrint}
              size="lg"
              variant="outline"
              className="font-semibold shadow-md hover:shadow-lg transition-all px-6 py-4 text-base"
              data-testid="print-quote-btn"
            >
              <Printer className="mr-2 h-5 w-5" />
              Print Quote
            </Button>
          </div>
        </div>

        {/* Mobile Sticky Action Bar - positioned above MobileBottomNav */}
        <div className="md:hidden fixed bottom-14 left-0 right-0 bg-white border-t border-gray-200 p-3 z-40 print-hidden">
          <div className="grid grid-cols-3 gap-2 max-w-md mx-auto">
            <Button
              onClick={handleSaveAsLead}
              size="sm"
              variant="outline"
              className="h-12 text-xs font-medium"
              data-testid="save-as-lead-btn-mobile"
            >
              Save
            </Button>

            <Button
              onClick={handleSendQuoteViaSMS}
              size="sm"
              className="h-12 text-xs font-medium bg-primary text-primary-foreground"
              data-testid="send-quote-sms-btn-mobile"
            >
              <Send className="w-4 h-4 mr-1" />
              Send
            </Button>

            <Button
              onClick={handleBookClient}
              size="sm"
              className="h-12 text-xs font-medium bg-green-600 text-white"
              data-testid="book-client-btn-mobile"
            >
              <CalendarCheck className="w-4 h-4 mr-1" />
              Book
            </Button>
          </div>
        </div>
      </main>

      {/* Tier 2 — Save Stale Guard Modal */}
      <SaveStaleGuardModal
        open={saveGuard.open}
        staleServices={saveGuard.staleList}
        isWorking={isRecalcing}
        onRecalcAndSave={async () => {
          const result = await recalculateAllStale();
          // Only proceed with save if recalc fully succeeded.
          if (result.ok) {
            const proceed = saveGuard.onProceed;
            setSaveGuard({ open: false, staleList: [], onProceed: null });
            await proceed?.();
          } else {
            // Keep modal open so the user can choose Save Anyway / Cancel.
            // Refresh stale list (some may have succeeded).
            setSaveGuard((g) => ({ ...g, staleList: [...staleServices] }));
          }
        }}
        onSaveAnyway={async () => {
          const proceed = saveGuard.onProceed;
          setSaveGuard({ open: false, staleList: [], onProceed: null });
          await proceed?.();
        }}
        onCancel={() => setSaveGuard({ open: false, staleList: [], onProceed: null })}
      />

      <footer className="print-hidden mt-16 py-8 border-t bg-card">
        <div className="container mx-auto px-4 text-center">
          <p className="text-sm text-muted-foreground">
            Powered by <span className="font-semibold text-primary">KRO</span>
          </p>
        </div>
      </footer>

      {/* Hidden printable quote layout */}
      <div className="print-only" style={{ display: 'none' }}>
        <PrintableQuote
          ref={printableQuoteRef}
          branding={branding}
          clientInfo={clientInfo}
          services={calculations || {}}
          totals={(() => {
            const order = ['roof', 'siding', 'gutter', 'driveway', 'deck', 'fence'];
            let subtotal = 0;
            let tax = 0;
            let discount = 0;
            order.forEach((key) => {
              const svc = calculations?.[key];
              if (!svc || !svc.result) return;
              const grand = Number(svc.result.grandTotal) || 0;
              if (grand <= 0) return;
              subtotal += Number(svc.result.subtotal) || 0;
              tax += Number(svc.result.tax) || 0;
              discount += Number(svc.result.discount) || 0;
            });
            return {
              subtotal,
              tax,
              discount,
              grandTotal: subtotal + tax,
            };
          })()}
        />
      </div>

      {/* Success Modal - CTA Feedback */}
      <Dialog 
        open={successModal.open} 
        onOpenChange={(open) => setSuccessModal(prev => ({ ...prev, open }))}
      >
        <DialogContent className="sm:max-w-md" data-testid="success-modal">
          <DialogHeader className="text-center sm:text-center">
            <div className={`mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full ${
              successModal.type === 'sms_sent' ? 'bg-blue-100' : 'bg-green-100'
            }`}>
              {successModal.type === 'sms_sent' ? (
                <MessageCircle className="h-10 w-10 text-blue-600" />
              ) : (
                <CheckCircle2 className="h-10 w-10 text-green-600" />
              )}
            </div>
            <DialogTitle className="text-xl">
              {successModal.type === 'sms_sent' 
                ? 'Quote Sent!' 
                : successModal.type === 'booked' 
                  ? 'Client Booked!' 
                  : 'Lead Saved!'}
            </DialogTitle>
            <DialogDescription className="text-base">
              {successModal.type === 'sms_sent'
                ? `Quote sent via SMS to ${successModal.recipientPhone}`
                : successModal.type === 'booked' 
                  ? `${successModal.clientName} has been booked and is ready to schedule.`
                  : `${successModal.clientName} has been saved as a lead with quote attached.`
              }
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col gap-2 sm:flex-col">
            {successModal.type === 'sms_sent' && successModal.conversationId && (
              <Button
                onClick={() => {
                  setSuccessModal({ open: false, type: null, leadId: null, clientName: null, conversationId: null, recipientPhone: null });
                  navigate(`/inbox?conversationId=${successModal.conversationId}`);
                }}
                className="w-full"
                data-testid="view-conversation-btn"
              >
                <MessageCircle className="mr-2 h-4 w-4" />
                View Conversation
              </Button>
            )}
            <Button
              onClick={() => {
                setSuccessModal({ open: false, type: null, leadId: null, clientName: null, conversationId: null, recipientPhone: null });
                navigate(`/pipeline?leadId=${successModal.leadId}`);
              }}
              className={successModal.type === 'sms_sent' ? 'w-full' : 'w-full'}
              variant={successModal.type === 'sms_sent' ? 'outline' : 'default'}
              data-testid="view-lead-btn"
            >
              <ExternalLink className="mr-2 h-4 w-4" />
              View Lead
            </Button>
            <Button
              variant="outline"
              onClick={() => setSuccessModal({ open: false, type: null, leadId: null, clientName: null, conversationId: null, recipientPhone: null })}
              className="w-full"
              data-testid="close-modal-btn"
            >
              Create Another Quote
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RoofEstimator;
